import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaEdit, FaTrashAlt } from "react-icons/fa";
import { deleteOfficeStaff } from "./supportContactService";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import "../assets/css/SupportContacts.css";
import "../assets/css/DigixLeads.css";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";

const STAFF_COLUMN_WIDTHS = [160, 120, 130, 160, 100, 120, 120, 90, 100];

const SUPPORT_STATUSES = ["Active", "Inactive"];

/**
 * SupportStaff
 *
 * Page component for the Staff tab in the Support module.
 * Displays staff support contacts with filters, table, and pagination.
 */
function SupportStaff({
  staffContacts = [],
  onRefresh,
  onAddContact,
  onViewContact,
  onEditContact,
  onDeleteContact,
  categories = [],
  rowsPerPage = 5
}) {
  const navigate = useNavigate();
  const role = (() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canAdd = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.DELETE, role);
  const [filters, setFilters] = useState({
    name: "",
    category: "",
    status: "All Statuses",
    freeSearch: ""
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const skipPageResetOnFiltersChangeRef = useRef(true);
  const [deletingId, setDeletingId] = useState(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const categoryDropdownRef = useRef(null);
  const { getColumnStyle, handleResizeStart } = useResizableColumns(STAFF_COLUMN_WIDTHS);

  useEffect(() => {
    if (skipPageResetOnFiltersChangeRef.current) {
      skipPageResetOnFiltersChangeRef.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [filters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
        setCategorySearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    const empty = { name: "", category: "", status: "All Statuses", freeSearch: "" };
    setFilters(empty);
    setSortColumn(null);
    setSortDirection("asc");
    setCategoryDropdownOpen(false);
    setCategorySearchQuery("");
    setCurrentPage(1);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this staff?")) return;
    setDeletingId(contactId);
    try {
      await deleteOfficeStaff(contactId);
      if (onRefresh) onRefresh();
      if (onDeleteContact) onDeleteContact(contactId);
    } catch (err) {
      console.error("Error deleting staff:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete staff.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredContacts = useMemo(() => {
    return staffContacts.filter((c) => {
      const name = (c.Staff_Name ?? c.staff_name ?? c.name ?? "").toLowerCase();
      const role = (c.Staff_Role ?? c.staff_role ?? "").toLowerCase();
      const status = (c.Status ?? c.status ?? "").toLowerCase();
      const phone = (c.Phone_Number ?? c.phone_number ?? c.Alternate_Phone ?? c.alternate_phone ?? "").toString();
      const email = (c.Email ?? c.email ?? "").toLowerCase();
      const f = filters;
      const supervisor = (c.Supervisor_Name ?? c.supervisor_name ?? "").toLowerCase();

      if (f.name && !name.includes(f.name.toLowerCase())) return false;
      if (f.category && !role.includes(f.category.toLowerCase())) return false;
      if (f.status && f.status !== "All Statuses") {
        if (status !== f.status.toLowerCase()) return false;
      }
      if (f.freeSearch) {
        const search = f.freeSearch.toLowerCase().trim();
        if (!search) return true;
        const matches =
          name.includes(search) ||
          role.includes(search) ||
          status.includes(search) ||
          phone.includes(search) ||
          email.includes(search) ||
          supervisor.includes(search);
        if (!matches) return false;
      }
      return true;
    });
  }, [staffContacts, filters]);

  const displayedContacts = useMemo(() => {
    if (!sortColumn) return filteredContacts;
    const val = (s, key) => {
      switch (key) {
        case "staffName":
          return String(s.Staff_Name ?? s.staff_name ?? s.name ?? "").toLowerCase();
        case "staffRole":
          return String(s.Staff_Role ?? s.staff_role ?? "").toLowerCase();
        case "phone":
          return String(
            s.Phone_Number ?? s.phone_number ?? s.Alternate_Phone ?? s.alternate_phone ?? ""
          ).toLowerCase();
        case "email":
          return String(s.Email ?? s.email ?? "").toLowerCase();
        case "shift": {
          const shiftStart = s.Shift_Start ?? s.shift_start ?? "";
          const shiftEnd = s.Shift_End ?? s.shift_end ?? "";
          return [shiftStart, shiftEnd].filter(Boolean).join(" ").toLowerCase();
        }
        case "workingDays":
          return String(s.Working_Days ?? s.working_days ?? "").toLowerCase();
        case "supervisor":
          return String(s.Supervisor_Name ?? s.supervisor_name ?? "").toLowerCase();
        case "status":
          return String(s.Status ?? s.status ?? "").toLowerCase();
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    const rowId = (s) =>
      Number(s.Staff_Id ?? s.staff_id ?? s.Id ?? s.id ?? 0);
    return [...filteredContacts].sort((a, b) => {
      const va = val(a, sortColumn);
      const vb = val(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return (rowId(a) - rowId(b)) * mult;
    });
  }, [filteredContacts, sortColumn, sortDirection]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const totalPages = Math.ceil(displayedContacts.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = displayedContacts.slice(startIndex, endIndex);
  const showPagination = displayedContacts.length > rowsPerPage;

  const handlePrevPage = () => setCurrentPage((p) => (p > 1 ? p - 1 : p));
  const handleNextPage = () => setCurrentPage((p) => (p < totalPages ? p + 1 : p));
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const handleAddClick = () => {
    if (onAddContact) onAddContact("Staff");
    else navigate("/support-contacts/staff/add");
  };

  const handleViewClick = (id) => {
    if (onViewContact) onViewContact(id, "staff");
    else navigate(`/support-contacts/staff/${id}/view`);
  };

  const handleEditClick = (id) => {
    if (onEditContact) onEditContact(id);
    else navigate(`/support-contacts/staff/${id}/edit`);
  };

  return (
    <div className="support-page-content">
      <div className="search-actions-row">
        <div className="search-filters-row">
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search by name..."
            value={filters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
          />
          <div className="domain-filter-dropdown" ref={categoryDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setCategoryDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCategoryDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.category || "All Roles"}</span>
              <FaChevronDown className={`domain-filter-chevron ${categoryDropdownOpen ? "open" : ""}`} />
            </div>
            {categoryDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search roles..."
                  value={categorySearchQuery}
                  onChange={(e) => setCategorySearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.category ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("category", "");
                      setCategoryDropdownOpen(false);
                      setCategorySearchQuery("");
                    }}
                  >
                    All Roles
                  </div>
                  {(() => {
                    const filteredCategories = (categories || []).filter(
                      (cat) =>
                        !categorySearchQuery.trim() ||
                        String(cat || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())
                    );
                    return filteredCategories.length > 0 ? (
                      filteredCategories.map((cat) => (
                        <div
                          key={cat}
                          className={`domain-filter-option ${filters.category === cat ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("category", cat);
                            setCategoryDropdownOpen(false);
                            setCategorySearchQuery("");
                          }}
                        >
                          {cat}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No roles found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <select
            className="search-filter-input search-filter-select"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Statuses</option>
            {SUPPORT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button type="button" className="btn-clear" onClick={handleClearFilters} style={{ marginLeft: 8 }}>
            Clear
          </button>
        </div>
        <div className="header-actions">
          {canAdd && (
            <button type="button" className="btn-primary btn-add-lead" onClick={handleAddClick}>
              Add Staff
            </button>
          )}
        </div>
      </div>

      <div className="digixleads-content">
        <div className="search-row-2">
          <div className="free-search-bar search-in-actions-row">
            <input
              type="text"
              className="free-search-input"
              placeholder="Search..."
              value={filters.freeSearch}
              onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
              title="Search across all columns"
            />
          </div>
        </div>

        <div className="table-wrapper">
          <div className="table-container">
            <table className="data-table leads-table-frozen support-table-resizable">
              <thead>
                <tr>
                  <th className="col-sticky col-0 support-th-sortable" style={getColumnStyle(0)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="staffName"
                        label="Staff Name"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(0, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(1)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="staffRole"
                        label="Staff Role"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(1, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(2)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="phone"
                        label="Phone"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(2, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(3)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="email"
                        label="Email"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(3, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(4)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="shift"
                        label="Shift"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(4, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(5)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="workingDays"
                        label="Working Days"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(5, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(6)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="supervisor"
                        label="Supervisor"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(6, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(7)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="status"
                        label="Status"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(7, e)} aria-hidden />
                  </th>
                  <th className="col-sticky col-action" style={getColumnStyle(8)}>
                    Actions
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(8, e)} aria-hidden />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="supportcontacts-empty-cell">
                      No staff yet.
                    </td>
                  </tr>
                ) : (
                  pageData.map((staff) => {
                    const id = staff.Staff_Id ?? staff.staff_id ?? staff.Id ?? staff.id;
                    const staffName = staff.Staff_Name ?? staff.staff_name ?? staff.name ?? "—";
                    const staffRole = staff.Staff_Role ?? staff.staff_role ?? "—";
                    const phone = staff.Phone_Number ?? staff.phone_number ?? staff.Alternate_Phone ?? staff.alternate_phone ?? "—";
                    const email = staff.Email ?? staff.email ?? "—";
                    const shiftStart = staff.Shift_Start ?? staff.shift_start ?? "";
                    const shiftEnd = staff.Shift_End ?? staff.shift_end ?? "";
                    const shift = [shiftStart, shiftEnd].filter(Boolean).join(" - ") || "—";
                    const workingDays = staff.Working_Days ?? staff.working_days ?? "—";
                    const supervisor = staff.Supervisor_Name ?? staff.supervisor_name ?? "—";
                    const status = staff.Status ?? staff.status ?? "—";

                    return (
                      <tr key={id}>
                        <td className="col-sticky col-0">
                          <span
                            className="table-name-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleViewClick(id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleViewClick(id);
                              }
                            }}
                          >
                            <strong>{staffName}</strong>
                          </span>
                        </td>
                        <td>{staffRole}</td>
                        <td>{phone}</td>
                        <td>{email}</td>
                        <td>{shift}</td>
                        <td>{workingDays}</td>
                        <td>{supervisor}</td>
                        <td>
                          <span className={`status-badge status-${(status || "").toLowerCase()}`}>
                            {status || "—"}
                          </span>
                        </td>
                        <td className="col-sticky col-action">
                          <div className="project-actions-cell">
                            {canEdit && (
                              <button
                                type="button"
                                className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                title="Edit staff"
                                onClick={() => handleEditClick(id)}
                              >
                                <FaEdit />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                title="Delete staff"
                                disabled={deletingId === id}
                                onClick={() => handleDeleteContact(id)}
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {showPagination && (
            <div className="pagination">
              <button
                className="pagination-btn pagination-arrow"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <FaChevronLeft />
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="page-number ellipsis">
                      ...
                    </span>
                  ) : (
                    <span
                      key={page}
                      className={`page-number ${currentPage === page ? "active" : ""}`}
                      onClick={() => page !== "..." && setCurrentPage(page)}
                    >
                      {page}
                    </span>
                  )
                )}
              </div>
              <button
                className="pagination-btn pagination-arrow"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <FaChevronRight />
              </button>
              <div className="pagination-info">
                {startIndex + 1}-{Math.min(endIndex, displayedContacts.length)} of {displayedContacts.length} items
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportStaff;
